import { textColorFields } from '../_shared/textColor.js';
import { animationFields } from '../_shared/universal.js';
import { WEIGHT_OPTIONS } from '../../blocks/_shared/blockFields.js';
/**
 * announcement-bar — a top announcement strip, its OWN selectable section (previously the announcement
 * bar only existed as buried settings inside the `header` section, which the merchant couldn't select
 * directly). Up to 3 announcement messages, each with an optional link; when there are 2+ messages and
 * `autoRotate` is on, the section becomes a tiny client island that rotates between them.
 *
 * COLOR: a small scheme select (accent / inverse / background / surface) re-binds the `--aqx-scheme-*`
 * contract via `data-scheme`, OR a `custom` pair of hex colors (resolved from a stored ColorValue, same
 * idiom as blocks/price `colorHex`). `surface` has no `[data-scheme]` rule of its own, so it maps to the
 * theme's surface/ink vars directly while keeping the scheme contract intact.
 *
 * VISIBILITY mirrors the block-level `visibility` select (always / mobile-only / desktop-only) and is
 * emitted as a scoped @media + @container(aqx-page) rule so it reflows in BOTH the live storefront and
 * the builder device-preview.
 *
 * v1→v2 adds `textSize` + `weight` — the strip's TYPE. Until now the announcement text was pinned to
 * `--aqx-theme-text-sm` and `--aqx-theme-fw-medium` with no knob at all: a seller could recolour the bar
 * but never make its message read louder or quieter. Both defaults reproduce those tokens EXACTLY —
 * `textSize` 14 renders `calc(14px * var(--aqx-theme-body-scale,1))`, which is what `--aqx-theme-text-sm`
 * expands to (tokens/theme.css), so a theme's type scale still multiplies the seller's number the way it
 * multiplied the token; `weight` unset keeps `var(--aqx-theme-fw-medium, 500)`. Defaulted ⇒ no migration
 * (`applyDefaults` fills them), and hydrating the defaults onto a stored bar renders the identical pixel.
 * `weight` is the SHARED block field id, so it reads with `readWeight()` and inherits the whole existing
 * `field.weight` / `opt.<n>` dictionary — no new inspector key in any of the eight languages.
 */
export const announcementBarSchema = {
    type: 'announcement-bar',
    name: 'Announcement bar',
    version: 2,
    templates: ['index', 'collection', 'page', 'product'],
    fields: [
        // ── Announcements (up to 3; only non-empty ones render) ──────────────────────
        {
            id: 'announcement1',
            type: 'text',
            label: 'Announcement 1',
            group: 'Announcements',
            default: 'Livraison offerte partout au Maroc',
            defaultKey: 'section.announcement_free_shipping',
        },
        {
            id: 'link1',
            type: 'link',
            label: 'Announcement 1 link',
            group: 'Announcements',
        },
        {
            id: 'announcement2',
            type: 'text',
            label: 'Announcement 2',
            group: 'Announcements',
            default: '',
        },
        {
            id: 'link2',
            type: 'link',
            label: 'Announcement 2 link',
            group: 'Announcements',
        },
        {
            id: 'announcement3',
            type: 'text',
            label: 'Announcement 3',
            group: 'Announcements',
            default: '',
        },
        {
            id: 'link3',
            type: 'link',
            label: 'Announcement 3 link',
            group: 'Announcements',
        },
        // ── Appearance ───────────────────────────────────────────────────────────────
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'Appearance',
            default: 'accent',
            options: [
                { value: 'accent', label: 'Accent' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'background', label: 'Background' },
                { value: 'surface', label: 'Surface' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customBg',
            type: 'color',
            label: 'Custom background',
            group: 'Appearance',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customGradientBg',
            type: 'gradient',
            label: 'Custom background gradient',
            group: 'Appearance',
            info: 'Overrides the custom background color when set.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customText',
            type: 'color',
            label: 'Custom text',
            group: 'Appearance',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'textAlignment',
            type: 'select',
            label: 'Text alignment',
            group: 'Appearance',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'textSize',
            type: 'range',
            label: 'Text size',
            group: 'Appearance',
            default: 14,
            min: 11,
            max: 24,
            step: 1,
            unit: 'px',
        },
        {
            id: 'weight',
            type: 'select',
            label: 'Font weight',
            group: 'Appearance',
            default: '',
            options: WEIGHT_OPTIONS,
        },
        {
            id: 'autoRotate',
            type: 'checkbox',
            label: 'Auto-rotate',
            group: 'Appearance',
            info: 'Rotate between announcements when there is more than one.',
            default: true,
        },
        {
            id: 'rotateSpeed',
            type: 'range',
            label: 'Rotate speed',
            group: 'Appearance',
            default: 5,
            min: 2,
            max: 10,
            step: 1,
            unit: 's',
            visibleWhen: { field: 'autoRotate', equals: true },
        },
        // ── Visibility ─────────────────────────────────────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Visibility',
            group: 'Visibility',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'desktop-hidden', label: 'Mobile only' },
                { value: 'mobile-hidden', label: 'Desktop only' },
            ],
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18).
        ...animationFields,
    ],
};
export const announcementBarPresets = [
    {
        name: 'Announcement bar',
        settings: {
            announcement2: '',
            announcement3: '',
            colorScheme: 'accent',
            customBg: { ref: 'theme', token: 'accent' },
            customText: { ref: 'custom', value: '#ffffff' },
            textAlignment: 'center',
            textSize: 14,
            weight: '',
            autoRotate: true,
            rotateSpeed: 5,
            visibility: 'always-display',
        },
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const announcementBarMeta = {
    label: 'Announcement bar',
    group: 'Layout',
    icon: 'layout',
};
//# sourceMappingURL=schema.js.map